/**
 * Represents a column in a JSON tabular data structure
 */
export interface ColumnJSON {
  /**
   * Unique identifier for the column
   */
  id: string;
  
  /**
   * Display name of the column
   */
  name: string;
  
  /**
   * Values in the column
   */
  values: string[];
}

/**
 * Represents a row in the JSON tabular data structure
 */
export interface DataRowJSON extends Record<string, string> {
  /**
   * Unique identifier for the row
   */
  id: string;
}

/**
 * Represents a JSON tabular data structure
 */
export class TableDataJSON {
  private _columns: ColumnJSON[];
  private _cachedRows: DataRowJSON[] | null = null;

  constructor(columns: ColumnJSON[]) {
    this._columns = columns;
  }

  /**
   * Get the columns of the table
   */
  get columns(): ColumnJSON[] {
    return this._columns;
  }

  /**
   * Set the columns of the table
   */
  set columns(newColumns: ColumnJSON[]) {
    this._columns = newColumns;
    this._cachedRows = null; // Invalidate cache
  }

  /**
   * Get the rows of the table, computed from columns
   * Uses caching to avoid recomputing unless columns change
   */
  get rows(): DataRowJSON[] {
    if (!this._cachedRows) {
      this._cachedRows = this._columnsToRows();
    }
    return this._cachedRows;
  }

  /**
   * Convert current columns to rows format
   * @private
   */
  private _columnsToRows(): DataRowJSON[] {
    if (!this._columns?.length) return [];
    const rowCount = this._getRowCount();
    const rows: DataRowJSON[] = [];

    for (let i = 0; i < rowCount; i++) {
      const row: DataRowJSON = { id: i.toString() };
      this._columns.forEach(col => {
        if (col?.id && Array.isArray(col.values)) {
          row[col.id] = col.values[i] || '';
        }
      });
      rows.push(row);
    }

    return rows;
  }

  /**
   * Get the number of rows based on column data
   * @private
   */
  private _getRowCount(): number {
    if (!this._columns?.length) return 0;
    const firstColumn = this._columns[0];
    if (!firstColumn?.values?.length) return 0;
    return firstColumn.values.length;
  }

  /**
   * Create a TableDataJSON instance from rows
   * @param rows Array of rows to convert to column-based format
   */
  static fromRows(rows: DataRowJSON[]): TableDataJSON {
    if (!rows.length) return new TableDataJSON([]);
    const firstRow = rows[0];
    if (!firstRow) return new TableDataJSON([]);

    // Get all unique column IDs (excluding 'id')
    const columnIds = Object.keys(firstRow).filter(key => key !== 'id');

    const columns = columnIds.map(id => ({
      id,
      name: id, // Use ID as name by default
      values: rows.map(row => row[id] || '')
    }));

    return new TableDataJSON(columns);
  }

  /**
   * Create an empty TableDataJSON instance
   */
  static empty(): TableDataJSON {
    return new TableDataJSON([]);
  }

  /**
   * Create a TableDataJSON instance from a partial data object
   * Useful for backward compatibility with code that expects the old interface
   */
  static from(data: { columns: ColumnJSON[]; rows?: DataRowJSON[] }): TableDataJSON {
    return new TableDataJSON(data.columns);
  }
}

/**
 * Props for components that handle JSON tabular data changes
 */
export interface TableDataChangeProps {
  /**
   * Callback when data changes
   */
  onDataChange?: (data: TableDataJSON) => void;
}

/**
 * Convert column-based data to row-based format
 */
export function columnsToRows(data: TableDataJSON): DataRowJSON[] {
  if (!data?.columns?.length) return [];
  const rowCount = getRowCount(data);
  const rows: DataRowJSON[] = [];

  for (let i = 0; i < rowCount; i++) {
    const row: DataRowJSON = { id: i.toString() };
    data.columns.forEach(col => {
      if (col?.id && Array.isArray(col.values)) {
        row[col.id] = col.values[i] || '';
      }
    });
    rows.push(row);
  }

  return rows;
}

/**
 * Convert row-based data to column-based format
 */
export function rowsToColumns(rows: DataRowJSON[]): ColumnJSON[] {
  if (!rows.length) return [];
  const firstRow = rows[0];
  if (!firstRow) return [];

  // Get all unique column IDs (excluding 'id')
  const columnIds = Object.keys(firstRow).filter(key => key !== 'id');

  return columnIds.map(id => ({
    id,
    name: id, // Use ID as name by default
    values: rows.map(row => row[id] || '')
  }));
}

/**
 * Get the number of rows in the table based on column data
 */
export function getRowCount(data: TableDataJSON): number {
  if (!data?.columns?.length) return 0;
  const firstColumn = data.columns[0];
  if (!firstColumn?.values?.length) return 0;
  return firstColumn.values.length;
} 